export const updatePageSEO = (config: {
  title: string;
  description: string;
  keywords: string;
  canonical: string;
  ogTitle?: string;
  ogDescription?: string;
  ogType?: string;
  jsonLd?: object;
}) => {
  // Update title
  document.title = config.title;

  // Update or create meta tags
  const updateMetaTag = (name: string, content: string, isProperty = false) => {
    const attribute = isProperty ? 'property' : 'name';
    let element = document.querySelector(`meta[${attribute}="${name}"]`);
    if (!element) {
      element = document.createElement('meta');
      element.setAttribute(attribute, name);
      document.head.appendChild(element);
    }
    element.setAttribute('content', content);
  };

  // Update basic meta tags
  updateMetaTag('description', config.description);
  updateMetaTag('keywords', config.keywords);

  // Update canonical link
  let canonical = document.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    document.head.appendChild(canonical);
  }
  canonical.setAttribute('href', config.canonical);

  // Update Open Graph tags
  updateMetaTag('og:title', config.ogTitle || config.title, true);
  updateMetaTag('og:description', config.ogDescription || config.description, true);
  updateMetaTag('og:type', config.ogType || 'website', true);
  updateMetaTag('og:url', config.canonical, true);

  // Update Twitter Card tags
  updateMetaTag('twitter:card', 'summary_large_image');
  updateMetaTag('twitter:title', config.ogTitle || config.title);
  updateMetaTag('twitter:description', config.ogDescription || config.description);

  // Update last-modified
  const today = new Date().toISOString().split('T')[0];
  updateMetaTag('last-modified', today);

  // Add or update JSON-LD
  if (config.jsonLd) {
    // Remove existing JSON-LD scripts added by this function
    const existingScripts = document.querySelectorAll('script[type="application/ld+json"][data-dynamic="true"]');
    existingScripts.forEach(script => script.remove());

    // Add new JSON-LD
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-dynamic', 'true');
    script.textContent = JSON.stringify(config.jsonLd);
    document.head.appendChild(script);
  }
};

// Get base URL from environment variable
const getBaseUrl = () => {
  return import.meta.env.VITE_SITE_URL || 'https://okayama-juku.example.com';
};

export const SEO_CONFIGS = {
  home: {
    title: '岡山市北区で志望校合格を実現する学習塾 | おかやま学習塾',
    description: '岡山市北区の小中高生向け個別指導塾。志望校合格率95%の実績。岡山朝日高校・岡山城東高校など難関校への合格者多数。一人ひとりに寄り添う完全個別カリキュラムで、地域密着型の入試対策を提供します。無料体験授業受付中。',
    keywords: '岡山市北区,学習塾,個別指導,入試対策,志望校合格,岡山朝日高校,岡山城東高校',
    canonical: `${getBaseUrl()}/`,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'EducationalOrganization',
        name: 'おかやま学習塾',
        description: '岡山市北区の小中高生向け個別指導塾。志望校合格率95%の実績で地域密着型の入試対策を提供。',
        url: `${getBaseUrl()}/`,
        telephone: '+81-86-012-3456',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '○○町1-2-3',
          addressLocality: '岡山市北区',
          addressRegion: '岡山県',
          postalCode: '700-0000',
          addressCountry: 'JP'
        },
        geo: {
          '@type': 'GeoCoordinates',
          latitude: '34.6617',
          longitude: '133.9191'
        },
        openingHoursSpecification: {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
          opens: '15:00',
          closes: '22:00'
        },
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '4.9',
          reviewCount: '127'
        }
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: '岡山市北区のどの学校の生徒が通っていますか？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '岡山市北区の小中高校から幅広く通塾いただいています。岡山市立岡山中央小学校、岡山市立石井中学校、岡山朝日高校、岡山城東高校など、地域の主要校の生徒様が多数在籍しています。各学校の進度や特色に合わせた指導を行っています。'
            }
          },
          {
            '@type': 'Question',
            name: '個別指導と集団指導の違いは何ですか？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '当塾は完全個別指導を採用しています。一人ひとりの学力、目標、性格に合わせたオーダーメイドのカリキュラムで指導します。岡山市北区の各学校の進度に合わせ、苦手分野を徹底的に克服し、得意分野をさらに伸ばすことができます。'
            }
          },
          {
            '@type': 'Question',
            name: '入試対策はいつから始めるべきですか？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '早ければ早いほど効果的ですが、いつからでも遅すぎることはありません。岡山市北区の入試傾向を熟知した講師が、現在の学力と志望校に合わせた最適なプランを提案します。中学3年生の夏からでも、効率的な学習で志望校合格を目指せます。'
            }
          },
          {
            '@type': 'Question',
            name: '無料体験授業では何をしますか？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: 'まず学力診断テストを実施し、現在の理解度を把握します。その後、実際の授業を体験いただき、指導方法や教室の雰囲気を確認できます。最後に保護者様と面談し、岡山市北区の入試情報や最適な学習プランをご提案します。'
            }
          },
          {
            '@type': 'Question',
            name: '授業料はどのくらいですか？',
            acceptedAnswer: {
              '@type': 'Answer',
              text: '学年や受講コマ数によって異なります。小学生は月額15,000円から、中学生は月額20,000円から、高校生は月額25,000円からとなっています。詳しくは無料体験授業の際にご説明いたします。岡山市北区で通いやすい料金設定を心がけています。'
            }
          }
        ]
      }
    ]
  },
  services: {
    title: '事業内容 - 小中高生向け個別指導 | おかやま学習塾',
    description: '岡山市北区の小中高生向け個別指導塾の事業内容。小学生コース、中学生コース、高校生コースをご用意。完全個別カリキュラムで志望校合格をサポート。定期テスト対策から受験対策まで幅広く対応します。',
    keywords: '事業内容,個別指導,小学生,中学生,高校生,岡山市北区,学習塾',
    canonical: `${getBaseUrl()}/services`,
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'おかやま学習塾の個別指導サービス',
      description: '岡山市北区の小中高生向け個別指導。完全個別カリキュラムで志望校合格をサポート。',
      provider: {
        '@type': 'EducationalOrganization',
        name: 'おかやま学習塾',
        url: `${getBaseUrl()}/`
      },
      areaServed: {
        '@type': 'City',
        name: '岡山市北区'
      },
      serviceType: '個別指導塾'
    }
  },
  about: {
    title: '会社概要 - 岡山市北区の地域密着型学習塾 | おかやま学習塾',
    description: 'おかやま学習塾の会社概要。2018年設立、岡山市北区で地域密着型の個別指導を提供。志望校合格率95%の実績。所在地、営業時間、アクセス情報をご案内します。',
    keywords: '会社概要,岡山市北区,学習塾,アクセス,営業時間',
    canonical: `${getBaseUrl()}/about`,
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      name: 'おかやま学習塾について',
      description: '岡山市北区で地域密着型の個別指導を提供する学習塾の会社概要',
      mainEntity: {
        '@type': 'EducationalOrganization',
        name: 'おかやま学習塾',
        foundingDate: '2018',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '○○町1-2-3',
          addressLocality: '岡山市北区',
          addressRegion: '岡山県',
          postalCode: '700-0000',
          addressCountry: 'JP'
        }
      }
    }
  },
  careers: {
    title: '求人情報 - 講師募集中 | おかやま学習塾',
    description: 'おかやま学習塾の求人情報。個別指導講師を募集中。正社員・アルバイト・パート歓迎。週2日から勤務可能。充実の研修制度で未経験者も安心。岡山市北区で子どもたちの未来を一緒に創りませんか。',
    keywords: '求人情報,講師募集,アルバイト,正社員,岡山市北区,学習塾',
    canonical: `${getBaseUrl()}/careers`,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'JobPosting',
        title: '個別指導講師（正社員）',
        description: '岡山市北区の小学生・中学生・高校生を対象とした個別指導。一人ひとりの学力や目標に合わせたカリキュラム作成、授業実施、学習相談、保護者面談などを担当していただきます。',
        datePosted: '2025-01-15',
        employmentType: 'FULL_TIME',
        hiringOrganization: {
          '@type': 'EducationalOrganization',
          name: 'おかやま学習塾',
          sameAs: `${getBaseUrl()}/`
        },
        jobLocation: {
          '@type': 'Place',
          address: {
            '@type': 'PostalAddress',
            streetAddress: '○○町1-2-3',
            addressLocality: '岡山市北区',
            addressRegion: '岡山県',
            postalCode: '700-0000',
            addressCountry: 'JP'
          }
        },
        baseSalary: {
          '@type': 'MonetaryAmount',
          currency: 'JPY',
          value: {
            '@type': 'QuantitativeValue',
            value: 250000,
            unitText: 'MONTH'
          }
        }
      },
      {
        '@context': 'https://schema.org',
        '@type': 'JobPosting',
        title: '個別指導講師（アルバイト）',
        description: '岡山市北区の小中高生への個別指導。得意科目を活かして、生徒様の学習をサポートしていただきます。',
        datePosted: '2025-01-15',
        employmentType: 'PART_TIME',
        hiringOrganization: {
          '@type': 'EducationalOrganization',
          name: 'おかやま学習塾',
          sameAs: `${getBaseUrl()}/`
        },
        jobLocation: {
          '@type': 'Place',
          address: {
            '@type': 'PostalAddress',
            streetAddress: '○○町1-2-3',
            addressLocality: '岡山市北区',
            addressRegion: '岡山県',
            postalCode: '700-0000',
            addressCountry: 'JP'
          }
        },
        baseSalary: {
          '@type': 'MonetaryAmount',
          currency: 'JPY',
          value: {
            '@type': 'QuantitativeValue',
            value: 1500,
            unitText: 'HOUR'
          }
        }
      }
    ]
  },
  blog: {
    title: 'ブログ・新着情報 - 入試情報と学習のコツ | おかやま学習塾',
    description: 'おかやま学習塾のブログ・新着情報。岡山市北区の入試情報、効果的な学習方法、合格体験記、教育コラムなど、受験に役立つ情報を発信しています。',
    keywords: 'ブログ,新着情報,入試情報,学習方法,合格体験記,岡山市北区',
    canonical: `${getBaseUrl()}/blog`,
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'Blog',
      name: 'おかやま学習塾ブログ',
      description: '岡山市北区の入試情報や学習のコツをお届けするブログ',
      publisher: {
        '@type': 'EducationalOrganization',
        name: 'おかやま学習塾',
        url: `${getBaseUrl()}/`
      }
    }
  },
  trial: {
    title: '無料体験授業申し込み - 学力診断と最適プランをご提案 | おかやま学習塾',
    description: 'おかやま学習塾の無料体験授業申し込みページ。岡山市北区で志望校合格を目指すなら、まずは無料体験授業をお試しください。学力診断テスト、実際の授業体験、保護者面談を実施します。',
    keywords: '無料体験,体験授業,申し込み,学力診断,岡山市北区,学習塾',
    canonical: `${getBaseUrl()}/trial`,
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: '無料体験授業申し込み',
      description: '岡山市北区の学習塾の無料体験授業申し込みページ',
      provider: {
        '@type': 'EducationalOrganization',
        name: 'おかやま学習塾',
        url: `${getBaseUrl()}/`
      }
    }
  }
};
